/**
 * Community Garden — Frontend Entry Point
 *
 * Routes between landing page, server select, and garden view.
 * CACHE_BUST: 2026-02-16-004
 */

import { Router } from "./router.js";
import { authService } from "./auth.js";
import {
  onOfflineChange,
  registerServiceWorker,
  unregisterServiceWorker,
} from "./service-worker-registration.js";
import { initErrorTracking, errorTracker } from "./error-tracking.js";
import { getOnboarding } from "./onboarding.js";
import { panelManager } from "./components/panel-manager.js";
import "@fontsource-variable/pixelify-sans/wght.css";
import "@fontsource/silkscreen/latin-400.css";
import "@fontsource/silkscreen/latin-700.css";
import "./components/connection-status.css";
import "./toast-styles.css";

// Type declaration for temporary auth data stored on window during OAuth callback
declare global {
  interface Window {
    __tempAuthData?: {
      user: {
        id: string;
        discord_id: string;
        username: string;
        avatar: string | null;
        discriminator: string;
        points: number;
        contributions: number;
      };
      tokenPair: {
        accessToken: string;
        expiresAt: string;
        tokenId: string;
      };
    };
    __gardenLoaded?: boolean;
    __gardenTimeoutId?: number;
    gardenOnboarding?: {
      restart: () => void;
      skip: () => void;
      start: () => void;
    };
  }
}

let offlineIndicatorTimer: number | null = null;
onOfflineChange((offline) => {
  const indicator = document.getElementById("offline-indicator");
  if (!indicator) return;

  if (offlineIndicatorTimer !== null) {
    window.clearTimeout(offlineIndicatorTimer);
    offlineIndicatorTimer = null;
  }

  if (offline) {
    indicator.style.display = "flex";
    indicator.classList.remove("hidden");
    return;
  }

  indicator.classList.add("hidden");
  offlineIndicatorTimer = window.setTimeout(() => {
    if (navigator.onLine) indicator.style.display = "none";
  }, 180);
});

if (import.meta.env.PROD) {
  void registerServiceWorker();
} else {
  // A previously installed worker can otherwise keep serving stale assets in dev.
  void unregisterServiceWorker();
}

/* ── init ─────────────────────────────────────────────── */
// GARDEN-162: Build version for cache busting
const BUILD_VERSION = "2026-02-16-004";
if (import.meta.env.DEV) {
  console.log("[APP] Build version:", BUILD_VERSION);
}

const appEl = document.getElementById("app")!;
const connEl = document.getElementById("connection")!;


// Hide HUD initially — garden view will show it
// SCOUT-083: Use visibility:hidden instead of display:none to avoid iOS Safari
// paint race on first SPA transition (display:none removes from compositor layer)
const hudEl = document.getElementById("hud");
if (hudEl) {
  hudEl.style.visibility = "hidden";
  hudEl.style.pointerEvents = "none";
}

// Ensure body allows scrolling for landing page initially
document.body.style.overflow = 'auto';
document.body.style.touchAction = 'auto';
(document.body.style as any).webkitOverflowScrolling = 'touch';
document.body.style.height = 'auto';

// Ensure app container allows scrolling for landing page initially
const appContainer = document.getElementById('app');
if (appContainer) {
  appContainer.style.overflow = 'auto';
  appContainer.style.height = 'auto';
  appContainer.style.minHeight = '100%';
}

// SCOUT-010: Safe UUID generation with fallback for environments where
// the Web Crypto API may be unavailable or restricted.
function generateTokenId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }
}

// GARDEN-157: Check for OAuth denial/error first
const hasOAuthError = authService.checkForOAuthError();

// SECURITY FIX: Handle secure cookie-based OAuth callback
const urlParams = new URLSearchParams(window.location.search);
const authSuccess = urlParams.get('auth_success');
const botInstallSuccess = urlParams.get('bot_install_success');

// Handle secure cookie-based auth callback
if (!hasOAuthError && authSuccess === 'true') {
  try {
    // Retrieve auth data from secure cookies via API
    const response = await fetch('/auth/cookie-login', {
      method: 'POST',
      credentials: 'include', // Include cookies
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const authData = await response.json();
      const { token, user } = authData;

      // GARDEN-163: Build TokenPair from secure auth data
      const tokenPair = {
        accessToken: token,
        expiresAt: authData.expiresAt || new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        tokenId: authData.tokenId || generateTokenId()
      };

      // Clear the URL parameters to remove auth_success
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // GARDEN-163: Set initial hash for router to pick up after start()
      localStorage.setItem('garden_post_auth_redirect', '#/servers');

      // Store auth data for immediate use
      window.__tempAuthData = { user, tokenPair };
    } else {
      console.error('Failed to retrieve auth data from cookies');
    }
  } catch (error) {
    console.error('Failed to process secure OAuth callback:', error);
  }
}

// Handle bot installation success (independent of auth)
if (botInstallSuccess === 'true') {
  try {
    localStorage.setItem('garden_bot_install_success', 'true');
    const guildId = urlParams.get('guild_id');
    if (guildId) {
      localStorage.setItem('garden_new_guild_id', guildId);
    }
  } catch (storageError) {
    // Ignore storage failures (private mode / quota exceeded) - bot install will still work
    if (import.meta.env.DEV) {
      console.warn('[main] Failed to persist bot install state:', storageError);
    }
  }

  // Clear the URL parameters
  const newUrl = window.location.pathname + window.location.hash;
  window.history.replaceState({}, '', newUrl);

  // Redirect to servers page
  window.location.hash = '#/servers';
}

// SCOUT-095: Update auth state BEFORE router start so isAuthenticated()
// returns true when renderServerSelect() checks it during the post-auth redirect.
// Previous order (auth AFTER router) caused first OAuth login to always fail —
// router saw no auth, rendered landing page, then auth state arrived too late.

// Use the cookie exchange result before the router evaluates the first route.
const tempAuthData = window.__tempAuthData;
if (tempAuthData) {
  try {
    authService.updateAuthState(tempAuthData.user, tempAuthData.tokenPair);
    // Clean up temporary data
    delete window.__tempAuthData;
  } catch (error) {
    console.error('Failed to update auth state before router start:', error);
  }
}

// Pixi rasterizes text into textures, so its fonts must be ready before the
// first garden frame. DOM text can swap safely, but a late Pixi swap cannot.
if (document.fonts) {
  await Promise.all([
    document.fonts.load("400 16px 'Pixelify Sans Variable'"),
    document.fonts.load("700 12px 'Silkscreen'"),
  ]).catch(() => undefined);
}

const router = new Router(appEl, connEl);
await router.start();

// GARDEN-UX-004: Initialize panel manager for unified ESC handling
panelManager.initialize();

// GARDEN-UX-006: Set canvas default pointer-events to auto
// This will be dynamically managed by panel manager when modals open/close
const canvas = document.querySelector('canvas#game-canvas') as HTMLCanvasElement | null;
if (canvas) {
  canvas.style.pointerEvents = 'auto';
}

// Mark app as loaded to cancel loading timeout
window.__gardenLoaded = true;
// Clear the loading timeout to prevent unnecessary timer
if (window.__gardenTimeoutId) {
  clearTimeout(window.__gardenTimeoutId);
  delete window.__gardenTimeoutId;
}

// GARDEN-130: Initialize error tracking
// PERF-7: off the critical path — first paint should never wait on telemetry.
const idle: (fn: () => void) => void =
  typeof (window as any).requestIdleCallback === 'function'
    ? (fn) => (window as any).requestIdleCallback(fn, { timeout: 3000 })
    : (fn) => setTimeout(fn, 250);
const user = authService.getState().user;
idle(() => initErrorTracking(undefined, user?.id));

// Track auth completion milestone
if (authService.isAuthenticated()) {
  errorTracker.trackMilestone('auth_complete', { method: 'existing_session' });
}

if (import.meta.env.DEV) {
  console.log("[garden] Community Garden started");
}

// GARDEN-204: Expose onboarding controls for UI hooks and support/debug actions
window.gardenOnboarding = {
  restart: () => getOnboarding().restart(),
  skip: () => getOnboarding().skip(),
  start: () => getOnboarding().start()
};
